import { createFileRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { AccountSettings } from '../../components/settings/AccountSettings';
import { BankAccountList } from '../../components/settings/BankAccountList';
import { CategoryList } from '../../components/settings/CategoryList';
import { CategoryRuleList } from '../../components/settings/CategoryRuleList';
import { IncomeList } from '../../components/settings/IncomeList';
import { TransferRuleList } from '../../components/settings/TransferRuleList';
import { trpc } from '../../lib/trpc';
import { usePageTitle } from '../../lib/usePageTitle';

const TABS = [
  { id: 'categories', label: 'Kategorier' },
  { id: 'income', label: 'Inkomster' },
  { id: 'rules', label: 'Kategoriregler' },
  { id: 'transfers', label: 'Överföringsregler' },
  { id: 'bank-accounts', label: 'Bankkonton' },
  { id: 'account', label: 'Konto' },
] as const;

type Tab = (typeof TABS)[number]['id'];

const TAB_PANEL_ID = 'settings-tabpanel';
const tabElementId = (tab: Tab) => `settings-tab-${tab}`;

const searchSchema = z.object({
  tab: z
    .enum([
      'categories',
      'income',
      'rules',
      'transfers',
      'bank-accounts',
      'account',
      // Old tabs, kept only so old links redirect: limits live on /plan,
      // and savings goals are gone (savings are what's moved, on /savings)
      'budgets',
      'savings',
    ])
    .catch('categories'),
});

export const Route = createFileRoute('/settings/')({
  validateSearch: searchSchema,
  beforeLoad: ({ search }) => {
    if (search.tab === 'budgets') throw redirect({ to: '/plan', replace: true });
    if (search.tab === 'savings') throw redirect({ to: '/savings', replace: true });
  },
  component: SettingsPage,
});

function SettingsTabBar({ activeTab, onSelect }: { activeTab: Tab; onSelect: (tab: Tab) => void }) {
  const listRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef(new Map<Tab, HTMLButtonElement>());
  const [overflow, setOverflow] = useState({ left: false, right: false });

  const updateOverflow = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setOverflow((current) =>
      current.left === left && current.right === right ? current : { left, right },
    );
  }, []);

  useEffect(() => {
    updateOverflow();
    window.addEventListener('resize', updateOverflow);
    return () => window.removeEventListener('resize', updateOverflow);
  }, [updateOverflow]);

  // Keep the active tab visible in the horizontally scrolling bar (e.g. deep links to ?tab=account).
  useEffect(() => {
    const list = listRef.current;
    const tab = tabRefs.current.get(activeTab);
    if (!list || !tab) return;
    const tabStart = tab.offsetLeft;
    const tabEnd = tabStart + tab.offsetWidth;
    const padding = 32;
    if (tabStart < list.scrollLeft) {
      list.scrollTo({ left: Math.max(0, tabStart - padding), behavior: 'smooth' });
    } else if (tabEnd > list.scrollLeft + list.clientWidth) {
      list.scrollTo({ left: tabEnd - list.clientWidth + padding, behavior: 'smooth' });
    }
  }, [activeTab]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = TABS.findIndex((tab) => tab.id === activeTab);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % TABS.length;
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + TABS.length) % TABS.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = TABS.length - 1;
    const next = nextIndex === null ? undefined : TABS[nextIndex]?.id;
    if (!next) return;
    event.preventDefault();
    onSelect(next);
    tabRefs.current.get(next)?.focus();
  };

  return (
    <div className="relative -mx-4 md:mx-0">
      <div
        ref={listRef}
        role="tablist"
        aria-label="Inställningar"
        onScroll={updateOverflow}
        onKeyDown={handleKeyDown}
        className="scrollbar-none relative flex snap-x scroll-px-4 overflow-x-auto overscroll-x-contain border-b border-border px-4 md:snap-none md:px-0"
      >
        {TABS.map((tab) => {
          const selected = activeTab === tab.id;
          return (
            <button
              type="button"
              role="tab"
              key={tab.id}
              id={tabElementId(tab.id)}
              ref={(el) => {
                if (el) tabRefs.current.set(tab.id, el);
                else tabRefs.current.delete(tab.id);
              }}
              aria-selected={selected}
              aria-controls={selected ? TAB_PANEL_ID : undefined}
              tabIndex={selected ? 0 : -1}
              onClick={() => onSelect(tab.id)}
              className={`flex h-11 shrink-0 snap-start items-center whitespace-nowrap border-b-2 px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset sm:h-auto sm:py-2 ${
                selected
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 left-0 w-8 bg-linear-to-r from-background to-transparent transition-opacity ${
          overflow.left ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 right-0 w-10 bg-linear-to-l from-background to-transparent transition-opacity ${
          overflow.right ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );
}

function SettingsPage() {
  const { tab } = useSearch({ from: '/settings/' });
  const activeTab: Tab = tab === 'budgets' || tab === 'savings' ? 'categories' : tab;
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const tabLabel = TABS.find((t) => t.id === activeTab)?.label ?? 'Inställningar';
  usePageTitle(`${tabLabel} – Inställningar`);

  const reconcileMutation = trpc.transaction.reconcileAll.useMutation({
    onSuccess: (result) => {
      void utils.insights.overview.invalidate();
      void utils.insights.categoryTransactions.invalidate();
      void utils.recurringBill.list.invalidate();
      void utils.recurringBill.history.invalidate();
      const total =
        result.billsAdded +
        result.billsRemoved +
        result.loansAdded +
        result.loansRemoved +
        result.incomeAdded +
        result.incomeRemoved +
        result.recurringFlagsUpdated;
      toast.success(
        total > 0 ? `Synkning klar — ${total} ändringar gjorda` : 'Allt är redan synkat',
      );
    },
    onError: (err) => toast.error(err.message),
  });

  const setActiveTab = (tab: Tab) => {
    void navigate({ from: '/settings/', search: { tab }, replace: true });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Inställningar</h1>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={reconcileMutation.isPending}
          onClick={() => reconcileMutation.mutate()}
        >
          {reconcileMutation.isPending ? (
            'Synkar…'
          ) : (
            <>
              <span aria-hidden="true">🔄</span> Synka allt
            </>
          )}
        </Button>
      </div>

      <SettingsTabBar activeTab={activeTab} onSelect={setActiveTab} />

      <div role="tabpanel" id={TAB_PANEL_ID} aria-labelledby={tabElementId(activeTab)}>
        {activeTab === 'categories' && <CategoryList />}
        {activeTab === 'income' && <IncomeList />}
        {activeTab === 'rules' && <CategoryRuleList />}
        {activeTab === 'transfers' && <TransferRuleList />}
        {activeTab === 'bank-accounts' && <BankAccountList />}
        {activeTab === 'account' && <AccountSettings />}
      </div>
    </div>
  );
}
